import {
  HeaterAssociatedSamplePointIds,
  HeaterSensorType,
  SamplePointWithHeaterAddOn,
  isHeaterSensorType
} from 'types/models/heater';
import SamplePoint, { RawSamplePoint, SensorTagId } from 'types/models/samplePoint';

import { checkIsHeaterHost } from './check-is-heater-host';

type HeaterHiddenKey = keyof HeaterAssociatedSamplePointIds;

const SENSOR_TYPE_TO_HIDDEN_KEY: Record<HeaterSensorType, HeaterHiddenKey> = {
  [SensorTagId.XTEND_HEATED_TROUGH_MACHINE_CYCLES]: 'cycle',
  [SensorTagId.XTEND_HEATED_TROUGH_POWER]: 'energy',
  [SensorTagId.XTEND_HEATED_TROUGH_STATUS_FLAGS]: 'onOff',
  [SensorTagId.XTEND_HEATED_TROUGH_SUPPLY_CURRENT]: 'supplyCurrent',
  [SensorTagId.XTEND_HEATED_TROUGH_SUPPLY_VOLTS]: 'supplyVoltage',
  [SensorTagId.XTEND_HEATED_TROUGH_TEMPERATURE]: 'temperature'
};

const getHiddenKey = (sensorType: HeaterSensorType): HeaterHiddenKey | undefined => {
  return SENSOR_TYPE_TO_HIDDEN_KEY[sensorType];
};

/**
 * Returns true if a heater sensor and a heater host are associated.
 */
const isMatchingHeaterSensorAndHost = (
  rawSP: RawSamplePoint,
  accumulatedSP: SamplePoint
) => {
  const isHeaterSensor = isHeaterSensorType(rawSP.sensorTags.id);
  const isHost = checkIsHeaterHost(accumulatedSP);
  const isOnSameMonitor = rawSP.deviceId === accumulatedSP.deviceId;

  return isHeaterSensor && isHost && isOnSameMonitor;
};

/**
 * Merge a heater sensor into a host.
 */
const mergeHeaterSensorIntoHost = (
  rawHeaterSP: RawSamplePoint,
  hostSP: SamplePoint
): SamplePointWithHeaterAddOn => {
  const mergeKey = getHiddenKey(rawHeaterSP.sensorTags.id as HeaterSensorType);
  if (!mergeKey) return hostSP;

  const enhancedHostSP: SamplePointWithHeaterAddOn = hostSP;

  enhancedHostSP._hidden = {
    ...(enhancedHostSP._hidden || {}), // Preserve existing associations
    heater: {
      ...(enhancedHostSP._hidden?.heater || {}), // Preserve existing associations
      [mergeKey]: rawHeaterSP.id
    }
  };

  return enhancedHostSP;
};

/**
 * Takes an array of parsed samplePoints and a raw samplePoint, and
 * returns an array of parsed samplePoints.
 * This reducer assumes that the host sample point (tank/trough) is accumulated first.
 */
export const heaterSamplePointsReducer = (
  accumulatedSamplePoints: SamplePoint[],
  currentHeaterSensor: RawSamplePoint
): SamplePoint[] => {
  const nextSamplePoints: SamplePoint[] = [];

  // Scan through the accumulated parsed sample points, if currentHeaterSensor pairs with a host sample point,
  // merge them. Otherwise, keep copying SP from accumulatedSamplePoints to nextSamplePoints array.
  for (const accumulatedSP of accumulatedSamplePoints) {
    if (isMatchingHeaterSensorAndHost(currentHeaterSensor, accumulatedSP)) {
      nextSamplePoints.push(mergeHeaterSensorIntoHost(currentHeaterSensor, accumulatedSP));
    } else {
      nextSamplePoints.push(accumulatedSP);
    }
  }

  // If currentHeaterSensor hasn't paired with any host, it means there's no host to associate with.
  // In this case, we drop the heater sensor since it's not useful stand-alone.

  return nextSamplePoints;
};